/**
 * Excel Template Generator for Form Builder multi-form import.
 *
 * Produces a downloadable .xlsx workbook where EACH SHEET (tab) represents a
 * single form. The columns match what `excel-to-json-converter` expects so the
 * generated template round-trips cleanly through the importer.
 *
 * Each row describes one question (or one answer-option row for
 * multi-option / conditional questions).
 */

import * as XLSX from 'xlsx';

/**
 * Column order for every sheet. Kept in sync with `ExcelRow` in
 * `excel-import.types.ts` and the parser in `excel-to-json-converter.ts`.
 */
export const TEMPLATE_COLUMNS = [
  'Form', // Section name
  'SectionOrder', // Section number (e.g. 1000, 2000)
  'QuestionOrder', // Unique question id within the form
  'questiontext', // The question / control label
  'Answer', // Answer option (leave blank for free-text / repeat per option)
  'NextQuestion', // Optional: q_id to branch to
  'Mandatory', // Y / N
  'Comments', // Free notes (ignored by importer)
] as const;

interface TemplateRow {
  Form: string;
  SectionOrder: string | number;
  QuestionOrder: string | number;
  questiontext: string;
  Answer?: string;
  NextQuestion?: string;
  Mandatory?: string;
  Comments?: string;
}

interface TemplateSheet {
  /** Sheet/tab name — becomes the form name on import. Max 31 chars (Excel limit). */
  sheetName: string;
  rows: TemplateRow[];
}

/**
 * Truncate/clean a sheet name to satisfy Excel's 31-char limit and forbidden
 * characters (: \ / ? * [ ]).
 */
const sanitiseSheetName = (name: string): string =>
  name
    .replace(/[:\\/?*[\]]/g, ' ')
    .trim()
    .slice(0, 31) || 'Form';

/**
 * "Field Types" example form — ONE worked question per supported control type
 * so the user can see the exact Answer keyword and row layout that produces
 * each field. Every control type the importer can infer is represented.
 */
const FIELD_TYPE_EXAMPLES: TemplateSheet = {
  sheetName: 'Field Types',
  rows: [
    // --- Text & numeric ---
    {
      Form: 'Text & Numbers',
      SectionOrder: 1000,
      QuestionOrder: 1010,
      questiontext: 'Job reference',
      Answer: 'Free Text',
      Mandatory: 'Y',
      Comments: 'TEXT: single-line free text.',
    },
    {
      Form: 'Text & Numbers',
      SectionOrder: 1000,
      QuestionOrder: 1020,
      questiontext: 'Describe the site conditions',
      Answer: 'Free Text',
      Mandatory: 'N',
      Comments:
        'TEXTAREA: "Free Text" + a keyword like describe/comment/note/observation/detail → multi-line.',
    },
    {
      Form: 'Text & Numbers',
      SectionOrder: 1000,
      QuestionOrder: 1030,
      questiontext: 'Number of hazards identified (XX)',
      Answer: 'Number',
      Mandatory: 'Y',
      Comments: 'NUMBER: "Number", or (XX)/(X.XX), or units like (m),(kg),(cm) in the text.',
    },
    {
      Form: 'Text & Numbers',
      SectionOrder: 1000,
      QuestionOrder: 1040,
      questiontext: 'Site contact number',
      Answer: 'Free Text',
      Mandatory: 'N',
      Comments: 'TEXT (numeric): "contact/phone number" is validated as a number.',
    },
    // --- Date & time ---
    {
      Form: 'Date & Time',
      SectionOrder: 2000,
      QuestionOrder: 2010,
      questiontext: 'Site visit date',
      Answer: 'Date',
      Mandatory: 'Y',
      Comments: 'DATE: "Date" (or dd/mm/yyyy).',
    },
    {
      Form: 'Date & Time',
      SectionOrder: 2000,
      QuestionOrder: 2020,
      questiontext: 'Start time',
      Answer: 'Time',
      Mandatory: 'N',
      Comments: 'TIME: "Time" (or hh:mm).',
    },
    {
      Form: 'Date & Time',
      SectionOrder: 2000,
      QuestionOrder: 2030,
      questiontext: 'Appointment date and time',
      Answer: 'Date Time',
      Mandatory: 'N',
      Comments: 'DATETIME: "Date Time" / "Date & Time".',
    },
    // --- Choice fields ---
    {
      Form: 'Choices',
      SectionOrder: 3000,
      QuestionOrder: 3010,
      questiontext: 'Access type',
      Answer: 'Pedestrian',
      Mandatory: 'Y',
      Comments: 'DROPDOWN (option): repeat the SAME QuestionOrder, one option per row.',
    },
    {
      Form: 'Choices',
      SectionOrder: 3000,
      QuestionOrder: 3010,
      questiontext: '',
      Answer: 'Vehicle',
      Comments: 'Continuation row — leave questiontext blank, add next option.',
    },
    {
      Form: 'Choices',
      SectionOrder: 3000,
      QuestionOrder: 3010,
      questiontext: '',
      Answer: 'Both',
      Comments: 'Continuation row — last option.',
    },
    {
      Form: 'Choices',
      SectionOrder: 3000,
      QuestionOrder: 3020,
      questiontext: 'Is a permit required?',
      Answer: 'Yes/No',
      NextQuestion: '3030',
      Mandatory: 'Y',
      Comments: 'CONDITIONAL: "Yes/No" makes a branching question. NextQuestion = where Yes leads.',
    },
    {
      Form: 'Choices',
      SectionOrder: 3000,
      QuestionOrder: 3030,
      questiontext: 'Permit number',
      Answer: 'Free Text',
      Mandatory: 'N',
      Comments: 'Target of the conditional branch above.',
    },
    // --- Capture / media ---
    {
      Form: 'Capture',
      SectionOrder: 4000,
      QuestionOrder: 4010,
      questiontext: 'Upload site photo',
      Answer: 'Attach Photos',
      Mandatory: 'N',
      Comments: 'IMAGE (s3_image): "Attach Photos"/"Photo"/"Image", or "photo/image" in the text.',
    },
    {
      Form: 'Capture',
      SectionOrder: 4000,
      QuestionOrder: 4020,
      questiontext: 'Engineer signature',
      Answer: 'Signature Field',
      Mandatory: 'Y',
      Comments: 'SIGNATURE: "Signature Field", or "signature" in the text.',
    },
    {
      Form: 'Capture',
      SectionOrder: 4000,
      QuestionOrder: 4030,
      questiontext: 'Pin drop the asset location',
      Answer: 'Map',
      Mandatory: 'N',
      Comments: 'MAP: "Map", or pin drop/coordinates/location in the text.',
    },
  ],
};

/**
 * Two further worked example forms showing how a real multi-section form is
 * laid out across tabs.
 */
const EXAMPLE_SHEETS: TemplateSheet[] = [
  FIELD_TYPE_EXAMPLES,
  {
    sheetName: 'Site Survey',
    rows: [
      // --- Section 1: Project Details ---
      {
        Form: 'Project Details',
        SectionOrder: 1000,
        QuestionOrder: 1010,
        questiontext: 'Project reference number',
        Answer: 'Free Text',
        Mandatory: 'Y',
        Comments: 'Free-text single line',
      },
      {
        Form: 'Project Details',
        SectionOrder: 1000,
        QuestionOrder: 1020,
        questiontext: 'Site visit date',
        Answer: 'Date',
        Mandatory: 'Y',
        Comments: 'Rendered as a date picker',
      },
      {
        Form: 'Project Details',
        SectionOrder: 1000,
        QuestionOrder: 1030,
        questiontext: 'Upload site photo',
        Answer: 'Attach Photos',
        Mandatory: 'N',
        Comments: 'Image upload control',
      },
      // --- Section 2: Access ---
      {
        Form: 'Access',
        SectionOrder: 2000,
        QuestionOrder: 2010,
        questiontext: 'Is site access restricted?',
        Answer: 'Yes/No',
        Mandatory: 'Y',
        Comments: 'Yes/No -> conditional branching question',
      },
      {
        Form: 'Access',
        SectionOrder: 2000,
        QuestionOrder: 2020,
        questiontext: 'Access type',
        Answer: 'Pedestrian',
        Mandatory: 'Y',
        Comments: 'Multi-option: repeat rows with the same QuestionOrder',
      },
      {
        Form: 'Access',
        SectionOrder: 2000,
        QuestionOrder: 2020,
        questiontext: '',
        Answer: 'Vehicle',
        Comments: 'Continuation row — leave questiontext blank',
      },
      {
        Form: 'Access',
        SectionOrder: 2000,
        QuestionOrder: 2020,
        questiontext: '',
        Answer: 'Both',
        Comments: 'Continuation row',
      },
      {
        Form: 'Access',
        SectionOrder: 2000,
        QuestionOrder: 2030,
        questiontext: 'Additional access notes',
        Answer: 'Free Text',
        Mandatory: 'N',
        Comments: 'Comment/observation -> textarea',
      },
    ],
  },
  {
    sheetName: 'Safety Checks',
    rows: [
      {
        Form: 'PPE',
        SectionOrder: 1000,
        QuestionOrder: 1010,
        questiontext: 'Is all required PPE available? (Mandatory)',
        Answer: 'Yes/No',
        Comments: '"(Mandatory)" in text also forces required',
      },
      {
        Form: 'PPE',
        SectionOrder: 1000,
        QuestionOrder: 1020,
        questiontext: 'Engineer signature',
        Answer: 'Signature Field',
        Mandatory: 'Y',
        Comments: 'Signature capture control',
      },
      {
        Form: 'Hazards',
        SectionOrder: 2000,
        QuestionOrder: 2010,
        questiontext: 'Number of hazards identified (XX)',
        Answer: 'Number',
        Mandatory: 'Y',
        Comments: 'Numeric input',
      },
      {
        Form: 'Hazards',
        SectionOrder: 2000,
        QuestionOrder: 2020,
        questiontext: 'Pin drop the hazard location',
        Answer: 'Map',
        Mandatory: 'N',
        Comments: 'Map / coordinates control',
      },
    ],
  },
];

/**
 * A one-sheet "Instructions" tab explaining each column and — crucially — a
 * worked EXAMPLE ROW for every supported field type. Placed first so it opens
 * by default. The "Field Types" tab holds the same examples as real,
 * importable rows.
 */
const buildInstructionsSheet = (): XLSX.WorkSheet => {
  const blank = ['', '', '', '', '', ''];
  const lines: string[][] = [
    ['Form Builder — XLSX Import Template'],
    blank,
    ['HOW IT WORKS'],
    ['• Each TAB (sheet) in this workbook becomes ONE new form.'],
    ['• The tab NAME becomes the form name.'],
    ['• Within a tab, the "Form" column groups questions into sections.'],
    ['• See the "Field Types" tab for one live example of every field type.'],
    ['• Delete the example tabs and add your own, or edit them in place.'],
    blank,
    ['COLUMNS'],
    ['Form', 'Section name (groups questions into a section).'],
    ['SectionOrder', 'Section number, e.g. 1000, 2000. Controls section order.'],
    ['QuestionOrder', 'Unique question id within the form. Controls question order.'],
    ['questiontext', 'The question / field label shown to the user.'],
    ['Answer', 'Answer type keyword OR an option value. See the FIELD TYPE EXAMPLES below.'],
    [
      'NextQuestion',
      'Optional. QuestionOrder to branch to (conditional forms). Use END to finish the form here and go to the summary.',
    ],
    ['Mandatory', 'Y = required, N = optional. Blank = optional.'],
    ['Comments', 'Free notes. Ignored by the importer.'],
    blank,
    // Header row for the per-type example table.
    ['FIELD TYPE EXAMPLES (copy the Answer value to get that field)'],
    ['Field type', 'questiontext example', 'Answer value', 'Notes'],
    ['Text (single line)', 'Job reference', 'Free Text', 'Default when Answer is blank too.'],
    [
      'Textarea (multi-line)',
      'Describe the site conditions',
      'Free Text',
      'Free Text + describe/comment/note/observation/detail keyword.',
    ],
    [
      'Number',
      'Number of hazards identified (XX)',
      'Number',
      'Also (XX), (X.XX), or units (m)/(kg)/(cm).',
    ],
    ['Date', 'Site visit date', 'Date', 'Or dd/mm/yyyy.'],
    ['Time', 'Start time', 'Time', 'Or hh:mm.'],
    ['Date + time', 'Appointment date and time', 'Date Time', 'Or "Date & Time".'],
    [
      'Dropdown (options)',
      'Access type',
      'Pedestrian',
      'Repeat SAME QuestionOrder per option; blank questiontext on continuation rows.',
    ],
    [
      'Conditional (Yes/No)',
      'Is a permit required?',
      'Yes/No',
      'Branching question; set NextQuestion to the follow-up QuestionOrder.',
    ],
    [
      'Image upload',
      'Upload site photo',
      'Attach Photos',
      'Also "Photo"/"Image", or photo/image in the text.',
    ],
    ['Signature', 'Engineer signature', 'Signature Field', 'Or "signature" in the text.'],
    ['Map / pin drop', 'Pin drop the asset location', 'Map', 'Or pin drop/coordinates/location.'],
    blank,
    ['MULTI-OPTION / DROPDOWN — HOW TO ADD OPTIONS'],
    [
      'Add extra rows with the SAME QuestionOrder, leave questiontext blank, and put each option in the Answer column (see the Field Types "Choices" section).',
    ],
    blank,
    ['CONDITIONAL (Yes/No) — HOW TO BRANCH'],
    [
      'Set Answer to "Yes/No" and put the follow-up question\'s QuestionOrder in NextQuestion. The follow-up is just another row/question in the same form.',
    ],
    [
      "To make an answer finish the form instead, put END in NextQuestion for that answer's row. The form goes straight to the summary and skips any later questions.",
    ],
    blank,
    ['MANDATORY'],
    [
      'Set Mandatory = Y, or include "(Mandatory)" in the questiontext. "(Optional)" forces optional.',
    ],
  ];
  const ws = XLSX.utils.aoa_to_sheet(lines);
  ws['!cols'] = [{ wch: 24 }, { wch: 34 }, { wch: 18 }, { wch: 62 }];
  return ws;
};

/**
 * Build a worksheet from template rows with a fixed column order and header.
 */
const buildFormSheet = (rows: TemplateRow[]): XLSX.WorkSheet => {
  const ws = XLSX.utils.json_to_sheet(rows, {
    header: TEMPLATE_COLUMNS as unknown as string[],
  });
  // Reasonable default column widths.
  ws['!cols'] = [
    { wch: 24 }, // Form
    { wch: 12 }, // SectionOrder
    { wch: 14 }, // QuestionOrder
    { wch: 48 }, // questiontext
    { wch: 20 }, // Answer
    { wch: 14 }, // NextQuestion
    { wch: 10 }, // Mandatory
    { wch: 52 }, // Comments
  ];
  return ws;
};

/**
 * Generate the multi-form XLSX template and trigger a browser download.
 *
 * @param filename Output file name (default `form-builder-template.xlsx`).
 * @param sheets   Optional custom sheets; defaults to the worked examples.
 */
export const downloadFormBuilderTemplate = (
  filename = 'form-builder-template.xlsx',
  sheets: TemplateSheet[] = EXAMPLE_SHEETS,
): void => {
  const workbook = XLSX.utils.book_new();

  // Instructions tab first.
  XLSX.utils.book_append_sheet(workbook, buildInstructionsSheet(), 'Instructions');

  // One tab per form.
  const usedNames = new Set<string>(['instructions']);
  sheets.forEach((sheet, idx) => {
    let name = sanitiseSheetName(sheet.sheetName);
    // Guard against duplicate sheet names (Excel forbids them).
    while (usedNames.has(name.toLowerCase())) {
      const suffix = ` (${idx + 1})`;
      name = `${name.slice(0, 31 - suffix.length)}${suffix}`;
    }
    usedNames.add(name.toLowerCase());
    XLSX.utils.book_append_sheet(workbook, buildFormSheet(sheet.rows), name);
  });

  XLSX.writeFile(workbook, filename);
};

export type { TemplateRow, TemplateSheet };
